import {
  ControllershipHubBackendClient,
  ControllershipHubBackendClientConfig,
  GetControlAttributesCommand,
  GetControlAttributesCommandInput,
  GetControlAttributesCommandOutput,
  GetControlAuditLogsCommand,
  GetControlAuditLogsCommandInput,
  GetControlAuditLogsCommandOutput,
  GetControlCodeAuditLogsCommand,
  GetControlCodeAuditLogsCommandInput,
  GetControlCodeAuditLogsCommandOutput,
  GetControlDataCommand,
  GetControlDataCommandInput,
  GetControlDataCommandOutput,
  GetControlsCommand,
  GetControlsCommandInput,
  GetControlsCommandOutput,
  GetHistoricalExecutionDataCommand,
  GetHistoricalExecutionDataCommandInput,
  GetHistoricalExecutionDataCommandOutput,
  GetSnapshotCommand,
  GetSnapshotCommandInput,
  GetSnapshotCommandOutput,
  GetUserPreferencesCommand,
  GetUserPreferencesCommandInput,
  GetUserPreferencesCommandOutput,
  UpdateControlDataCommand,
  UpdateControlDataCommandInput,
  UpdateControlDataCommandOutput,
  UpdateUserPreferencesCommand,
  UpdateUserPreferencesCommandInput,
  UpdateUserPreferencesCommandOutput,
  GetSnapshotInput,
  PreviewDerivedUpdatesCommand,
  PreviewDerivedUpdatesCommandInput,
  PreviewDerivedUpdatesCommandOutput,
  // Procurement operations
  GetControlPivotDataCommand,
  GetControlPivotDataCommandInput,
  GetControlPivotDataCommandOutput,
  GetControlDrillDownDataCommand,
  GetControlDrillDownDataCommandInput,
  GetControlDrillDownDataCommandOutput,
  GetControlSummaryMetadataCommand,
  GetControlSummaryMetadataCommandInput,
  GetControlSummaryMetadataCommandOutput,
  GetDistinctValuesForFilterCommand,
  GetDistinctValuesForFilterCommandInput,
  GetDistinctValuesForFilterCommandOutput,
  GetAnalyticsDashboardConfigCommand,
  GetAnalyticsDashboardConfigCommandInput,
  GetAnalyticsDashboardConfigCommandOutput,
  GetAnalyticsChartDataCommand,
  GetAnalyticsChartDataCommandInput,
  GetAnalyticsChartDataCommandOutput,
  GetCustomGridConfigsCommand,
  GetCustomGridConfigsCommandInput,
  GetCustomGridConfigsCommandOutput,
  SaveCustomGridConfigCommand,
  SaveCustomGridConfigCommandInput,
  SaveCustomGridConfigCommandOutput,
  DeleteCustomGridConfigCommand,
  DeleteCustomGridConfigCommandInput,
  DeleteCustomGridConfigCommandOutput,
  GetSharedUserViewsCommand,
  GetSharedUserViewsCommandInput,
  GetSharedUserViewsCommandOutput,
  UpdateSharedUserViewsCommand,
  UpdateSharedUserViewsCommandInput,
  UpdateSharedUserViewsCommandOutput,
  UpdateSharedViewStatusCommand,
  UpdateSharedViewStatusCommandInput,
  UpdateSharedViewStatusCommandOutput,
  CreateExportCommand,
  CreateExportCommandInput,
  CreateExportCommandOutput,
  ListExportsCommand,
  ListExportsCommandInput,
  ListExportsCommandOutput,
  GetExportCommand,
  GetExportCommandInput,
  GetExportCommandOutput,
} from '@amzn/controllershiphub-typescript-client';
import { HttpRequest } from '@aws-sdk/protocol-http';
import { Auth } from 'aws-amplify';
import { DEFAULT_CLIENT_CONFIG, constructApiUrl, getCurrentEnvironment } from './config';
import { apiCall } from 'src/utils/ApiCall';

export class ControllershipHub {
  private readonly client: ControllershipHubBackendClient;

  constructor(config: Partial<ControllershipHubBackendClientConfig> = {}) {
    const chubConfig = {
      ...DEFAULT_CLIENT_CONFIG,
      ...config,
      endpoint: config.endpoint ?? constructApiUrl(getCurrentEnvironment()),
    };

    this.client = this.initializeClient(chubConfig);
  }

  private initializeClient(
    config: ControllershipHubBackendClientConfig
  ): ControllershipHubBackendClient {
    const client = new ControllershipHubBackendClient(config);

    client.middlewareStack.add(
      (next) => async (args) => {
        if (!HttpRequest.isInstance(args.request)) {
          return next(args);
        }

        try {
          const session = await Auth.currentSession();
          const token = session.getIdToken().getJwtToken();

          args.request.headers = {
            'Content-Type': 'application/json',
            ...args.request.headers,
            Authorization: token,
          };
        } catch (error) {
          console.error('Authentication failed:', error);
          throw new Error('Failed to authenticate request');
        }

        return next(args);
      },
      {
        step: 'build',
        name: 'authMiddleware',
        priority: 'high',
      }
    );

    return client;
  }

  private validateControlId(input: { controlId?: string }): void {
    if (!input.controlId) {
      throw new Error('Control ID is required');
    }
  }

  async getControlAuditLogs(
    input: GetControlAuditLogsCommandInput
  ): Promise<GetControlAuditLogsCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetControlAuditLogsCommand(input));
  }

  async getControlCodeAuditLogs(
    input: GetControlCodeAuditLogsCommandInput
  ): Promise<GetControlCodeAuditLogsCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetControlCodeAuditLogsCommand(input));
  }
  async getControlAttributes(
    input: GetControlAttributesCommandInput
  ): Promise<GetControlAttributesCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetControlAttributesCommand(input));
  }

  async getControlData(input: GetControlDataCommandInput): Promise<GetControlDataCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetControlDataCommand(input));
  }

  async getControls(input: GetControlsCommandInput = {}): Promise<GetControlsCommandOutput> {
    return this.client.send(new GetControlsCommand(input));
  }

  async getHistoricalExecutionData(
    input: GetHistoricalExecutionDataCommandInput
  ): Promise<GetHistoricalExecutionDataCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetHistoricalExecutionDataCommand(input));
  }

  async updateControlData(
    input: UpdateControlDataCommandInput
  ): Promise<UpdateControlDataCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new UpdateControlDataCommand(input));
  }

  async getUserPreferences(
    input: GetUserPreferencesCommandInput
  ): Promise<GetUserPreferencesCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetUserPreferencesCommand(input));
  }

  async updateUserPreferences(
    input: UpdateUserPreferencesCommandInput
  ): Promise<UpdateUserPreferencesCommandOutput> {
    return this.client.send(new UpdateUserPreferencesCommand(input));
  }

  async getGRCReviewCycleSnapshotUrl(
    input: GetSnapshotCommandInput
  ): Promise<GetSnapshotCommandOutput> {
    return this.client.send(new GetSnapshotCommand(input));
  }

  async previewDerivedUpdates(
    input: PreviewDerivedUpdatesCommandInput
  ): Promise<PreviewDerivedUpdatesCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new PreviewDerivedUpdatesCommand(input));
  }

  // --- Procurement operations ---

  async getControlPivotData(
    input: GetControlPivotDataCommandInput
  ): Promise<GetControlPivotDataCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetControlPivotDataCommand(input));
  }

  async getControlDrillDownData(
    input: GetControlDrillDownDataCommandInput
  ): Promise<GetControlDrillDownDataCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetControlDrillDownDataCommand(input));
  }

  async getControlSummaryMetadata(
    input: GetControlSummaryMetadataCommandInput
  ): Promise<GetControlSummaryMetadataCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetControlSummaryMetadataCommand(input));
  }

  async getDistinctValuesForFilter(
    input: GetDistinctValuesForFilterCommandInput
  ): Promise<GetDistinctValuesForFilterCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetDistinctValuesForFilterCommand(input));
  }

  async getAnalyticsDashboardConfig(
    input: GetAnalyticsDashboardConfigCommandInput
  ): Promise<GetAnalyticsDashboardConfigCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetAnalyticsDashboardConfigCommand(input));
  }

  async getAnalyticsChartData(
    input: GetAnalyticsChartDataCommandInput
  ): Promise<GetAnalyticsChartDataCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetAnalyticsChartDataCommand(input));
  }

  async getCustomGridConfigs(
    input: GetCustomGridConfigsCommandInput
  ): Promise<GetCustomGridConfigsCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetCustomGridConfigsCommand(input));
  }

  async saveCustomGridConfig(
    input: SaveCustomGridConfigCommandInput
  ): Promise<SaveCustomGridConfigCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new SaveCustomGridConfigCommand(input));
  }

  async deleteCustomGridConfig(
    input: DeleteCustomGridConfigCommandInput
  ): Promise<DeleteCustomGridConfigCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new DeleteCustomGridConfigCommand(input));
  }

  async getSharedUserViews(
    input: GetSharedUserViewsCommandInput
  ): Promise<GetSharedUserViewsCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new GetSharedUserViewsCommand(input));
  }

  async updateSharedUserViews(
    input: UpdateSharedUserViewsCommandInput
  ): Promise<UpdateSharedUserViewsCommandOutput> {
    return this.client.send(new UpdateSharedUserViewsCommand(input));
  }

  async updateSharedViewStatus(
    input: UpdateSharedViewStatusCommandInput
  ): Promise<UpdateSharedViewStatusCommandOutput> {
    return this.client.send(new UpdateSharedViewStatusCommand(input));
  }

  async createExport(input: CreateExportCommandInput): Promise<CreateExportCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new CreateExportCommand(input));
  }

  async listExports(input: ListExportsCommandInput): Promise<ListExportsCommandOutput> {
    this.validateControlId(input);
    return this.client.send(new ListExportsCommand(input));
  }

  async getExport(input: GetExportCommandInput): Promise<GetExportCommandOutput> {
    return this.client.send(new GetExportCommand(input));
  }
}

export const controllershipHub = new ControllershipHub();
export default controllershipHub;
